import { useRef, useState } from 'react';
import { Section } from '@/components/containers/section';
import { PageContainer } from '@/components/containers/page-container';
import { HostedPciIframe, useHostedPciPayment } from '@/components/payment/hostedpci-net-iframe';
import { HostedPciTokens } from '@/types/hostedpci';
import { TextInput } from '@/components/inputs/text-input';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Form } from '@/components/form';
import { Banner } from '@/components/banner';
import { useBranding } from '@/outlets/branding-outlet';
import { useCreateEncountersPaymentsHostedpci } from '@/api/create-hosted-pci-payment';
import { useTranslation } from '@/contexts/i18n-context';
import {
  requiredAddress,
  requiredState,
  requiredString,
  requiredZipCode,
  zt,
} from '@/util/validators';
import { GradientFormTitle } from '@/components/containers/gradient-form-title';
import clsx from 'clsx';
import { Checkbox } from '@/components/inputs/checkbox';
import { Link, Navigate, useNavigate } from '@/util/react-router-dom';
import styles from './hostedpci-payment-page.module.css';
import { useEncounter } from '@/contexts/encounter-context';
import { useGetEncountersPrice } from '@/api/get-encounter-price';
import { StateSelector } from '@/components/form-controls/state-selector';
import { YearSelector } from '@/components/form-controls/year-selector';
import { MonthSelector } from '@/components/form-controls/month-selector';
import { FormButtonRow } from '@/components/containers/form-button-row';
import { useMediaQuery } from '@/hooks/use-media-query';
import { VisitTitleButtons } from '@/components/navigation/visit-title-buttons';
import { VisitNavigation } from '@/components/navigation/visit-navigation';
import { SaveProgressButton } from '@/components/navigation/save-progress-button';
import { Button } from '@/components/button';
import { formatPrice } from '@/util/formats';
import { termsOfUseHostedPciLink } from '@/util/urls';
import { CreateHostedPciPaymentErrorType, GetEncounterPriceResponse } from '@/api/api.schemas';
import { Loader } from '@/components/loaders/loader';
import { CardIconsContainer } from '../card-icons-container';

const translationBase = 'visit.hostedPciPayment';
const paymentTranslationBase = 'visit.payment';

const FreeVisitSchema = z.object({
  isFreeVisit: z.literal(true),
});

const PaidVisitSchema = z.object({
  isFreeVisit: z.literal(false),
  firstName: requiredString(`${translationBase}.creditCard.firstName.error`),
  lastName: requiredString(`${translationBase}.creditCard.lastName.error`),
  address1: requiredAddress(),
  city: requiredString(),
  state: requiredState(),
  zip: requiredZipCode(),
  creditCardExpMonth: z.string().min(1, zt(`${translationBase}.creditCard.month.error`)),
  creditCardExpYear: z.string().min(4, zt(`${translationBase}.creditCard.year.error`)),
  termsOfUse: z.boolean().refine((val) => val === true, {
    message: zt(`${translationBase}.terms.error`),
  }),
});

const PaymentFormSchema = z.discriminatedUnion('isFreeVisit', [FreeVisitSchema, PaidVisitSchema]);
type FormValues = z.infer<typeof PaymentFormSchema>;

const yearsToShow = 10;

export function HostedPciPaymentPage() {
  const { t } = useTranslation();
  const { encounter } = useEncounter();
  const {
    data: encounterPrice,
    error: encounterPriceError,
    isFetching,
  } = useGetEncountersPrice(encounter.id);

  if (isFetching) {
    return <Loader type="large" />;
  }

  if (encounterPriceError) {
    return (
      <PageContainer>
        <GradientFormTitle>
          <h1 className="h3">{t(`${translationBase}.title`)}</h1>
        </GradientFormTitle>
        <Section>
          <Banner type="error">{t(`${translationBase}.error.priceNotFound`)}</Banner>
        </Section>
      </PageContainer>
    );
  }

  if (!encounterPrice) {
    return <Navigate to="/system-error" />;
  }

  const isFreeVisit = encounterPrice.adjustedAmount === 0;

  return <HostedPciPaymentForm isFreeVisit={isFreeVisit} encounterPrice={encounterPrice} />;
}

interface HostedPciPaymentFormProps {
  isFreeVisit: boolean;
  encounterPrice: GetEncounterPriceResponse;
}

export function HostedPciPaymentForm({ isFreeVisit, encounterPrice }: HostedPciPaymentFormProps) {
  const { t } = useTranslation();
  const [paymentError, setPaymentError] = useState<string>();
  const { hostedPciConfiguration } = useBranding();
  const paymentErrorRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { encounter } = useEncounter();

  const { mutate: createHostedPciPayment, isPending: isPaymentProcessing } =
    useCreateEncountersPaymentsHostedpci();

  const isMobile = useMediaQuery('--screen-md');
  const currentYear = new Date().getFullYear();

  const {
    iFrameFormId,
    error: tokenError,
    isProcessing,
    requestTokenization,
    handleTokensReceived,
    handleError: onError,
  } = useHostedPciPayment();

  const form = useForm<FormValues>({
    resolver: zodResolver(PaymentFormSchema),
    defaultValues: {
      isFreeVisit,
      firstName: '',
      lastName: '',
      address1: '',
      city: '',
      state: '',
      zip: '',
      creditCardExpMonth: '',
      creditCardExpYear: '',
    },
  });

  const scrollToError = (ref: React.RefObject<HTMLDivElement | null>) => {
    if (ref.current) {
      ref.current.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      });
      ref.current.focus();
    }
  };

  const onTokensReceived = (receivedTokens: HostedPciTokens) => {
    handleTokensReceived(receivedTokens);
  };

  const onSubmit = async (data: FormValues) => {
    setPaymentError(undefined);
    let paymentData = {};

    if (!isFreeVisit) {
      const receivedTokens = await requestTokenization();

      paymentData = {
        creditCardNum: receivedTokens.ccNum,
        creditCardCvv: receivedTokens.ccCVV,
        creditCardBin: receivedTokens.ccBIN,

        ...data,
      };
    } else {
      paymentData = {
        isFreeVisit: true,
      };
    }

    createHostedPciPayment(
      {
        encounterId: encounter.id,
        data: paymentData,
      },
      {
        onSuccess: (data) => {
          navigate(`/visit/${encounter.id}/payment/success`, {
            state: data,
          });
        },
        onError: (error) => {
          let errorMessage: string;
          switch (error.type) {
            case CreateHostedPciPaymentErrorType.AlreadySubmitted:
              errorMessage = t(`${paymentTranslationBase}.alreadySubmitted`);
              break;
            case CreateHostedPciPaymentErrorType.InvalidVisitStatus:
              errorMessage = t(`${paymentTranslationBase}.invalidVisitStatus`);
              break;
            default:
              errorMessage = error.responseStatusDescription ?? t('common.errors.unknown');
              break;
          }

          setPaymentError(errorMessage);
          scrollToError(paymentErrorRef);
        },
      },
    );
  };

  return (
    <PageContainer>
      {!isMobile && <VisitTitleButtons />}
      <GradientFormTitle>
        {isMobile && <VisitTitleButtons />}
        <VisitNavigation />
        <h1 className="h3">{t(`${paymentTranslationBase}.title`)}</h1>
        <p>{t(`${paymentTranslationBase}.description`)}</p>
        {isMobile && <SaveProgressButton />}
      </GradientFormTitle>

      <Form id={iFrameFormId} onSubmit={onSubmit} form={form}>
        <Section>
          {tokenError instanceof Error && <Banner type="error">{tokenError.message}</Banner>}

          <div className={styles.priceContainer}>
            <div>
              <h2 className={clsx('overline', styles.sectionTitle)}>
                {t(`${paymentTranslationBase}.sectionTitle`)}
              </h2>
              <div className={styles.divider} />
            </div>
            {encounterPrice?.adjustedAmount !== undefined && (
              <div className={clsx('body-1', 'bold', styles.row)}>
                <span>{t(`${paymentTranslationBase}.yourCost`)}</span>
                <span>{formatPrice(encounterPrice.adjustedAmount)}</span>
              </div>
            )}
          </div>

          <div ref={paymentErrorRef}>
            {paymentError && <Banner type="error">{paymentError}</Banner>}
          </div>

          <div>
            <div className={clsx(styles.titleSection, isFreeVisit && styles.disabled)}>
              <span className="semibold">{t(`${translationBase}.payViaCard`)}</span>
              <CardIconsContainer />
            </div>
            {!isFreeVisit && (
              <div className={styles.iframeContainer}>
                <h2 className="h4">{t(`${translationBase}.creditCard.title`)}</h2>

                {hostedPciConfiguration &&
                  hostedPciConfiguration.location &&
                  hostedPciConfiguration.siteId &&
                  hostedPciConfiguration.reportCCType &&
                  hostedPciConfiguration.reportCCDigits &&
                  hostedPciConfiguration.reportCVVDigits && (
                    <HostedPciIframe
                      config={{
                        hostedPciLocation: hostedPciConfiguration.location,
                        hostedPciSiteId: hostedPciConfiguration.siteId,
                        hostedPciReportCCType: hostedPciConfiguration.reportCCType,
                        hostedPciReportCCDigits: hostedPciConfiguration.reportCCDigits,
                        hostedPciReportCVVDigits: hostedPciConfiguration.reportCVVDigits,
                      }}
                      onTokensReceived={onTokensReceived}
                      onError={onError}
                    />
                  )}

                <div className={styles.formInputs}>
                  <MonthSelector
                    name="creditCardExpMonth"
                    label={t(`${translationBase}.creditCard.month.label`)}
                    placeholder={t(`${translationBase}.creditCard.month.placeholder`)}
                  />

                  <YearSelector
                    name="creditCardExpYear"
                    label={t(`${translationBase}.creditCard.year.label`)}
                    placeholder={t(`${translationBase}.creditCard.year.placeholder`)}
                    startYear={currentYear}
                    endYear={currentYear + yearsToShow}
                  />
                </div>

                <div className={styles.formInputs}>
                  <TextInput
                    name="firstName"
                    label={t(`${translationBase}.creditCard.firstName.label`)}
                    placeholder={t(`${translationBase}.creditCard.firstName.placeholder`)}
                  />
                  <TextInput
                    name="lastName"
                    label={t(`${translationBase}.creditCard.lastName.label`)}
                    placeholder={t(`${translationBase}.creditCard.lastName.placeholder`)}
                  />
                </div>

                <h2 className="h4">{t(`${translationBase}.billing.title`)}</h2>

                <div className={styles.formInputs}>
                  <TextInput
                    name="address1"
                    label={t(`${translationBase}.billing.address.label`)}
                    placeholder={t(`${translationBase}.billing.address.placeholder`)}
                    className={styles.fullRow}
                  />

                  <TextInput
                    name="city"
                    label={t(`${translationBase}.billing.city.label`)}
                    placeholder={t(`${translationBase}.billing.city.placeholder`)}
                  />
                  <StateSelector
                    name="state"
                    label={t(`${translationBase}.billing.state.label`)}
                    placeholder={t(`${translationBase}.billing.state.placeholder`)}
                  />
                  <TextInput
                    name="zip"
                    label={t(`${translationBase}.billing.zip.label`)}
                    placeholder={t(`${translationBase}.billing.zip.placeholder`)}
                  />
                </div>
              </div>
            )}

            {!isFreeVisit && (
              <p className={clsx(styles.fullRow, styles.support)}>
                {t(`${paymentTranslationBase}.support.text`, {
                  link: (
                    <Button variant="link" as="a" to="/support" key="contact-support-link">
                      {t(`${paymentTranslationBase}.support.link`)}
                    </Button>
                  ),
                })}
              </p>
            )}
          </div>
        </Section>
        {!isFreeVisit && (
          <div className={styles.checkBoxContainer}>
            <Checkbox
              label={t(`${translationBase}.terms.agreement`, {
                link: (
                  <Link to={termsOfUseHostedPciLink} target="_blank" key="hostedpci-terms-link">
                    {t(`${translationBase}.terms.link`)}
                  </Link>
                ),
              })}
              className={styles.fullRow}
              name="termsOfUse"
            />
          </div>
        )}
        <FormButtonRow
          loading={isPaymentProcessing || isProcessing}
          disabled={isPaymentProcessing || isProcessing}
          buttonTextKey={`${paymentTranslationBase}.${isFreeVisit ? 'payFreeBtn' : 'payBtn'}`}
          type="submit"
        />
      </Form>
    </PageContainer>
  );
}
